import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { v4 as uuidv4 } from 'uuid';

// --- Interfaces ---
export interface FileInfo {
  name: string;
  nameWithoutExtension: string;
  extension: string;
  mime: string;
  size: number;
  readableSize: string;
  previewType: 'img' | 'video' | 'audio' | 'iframe' | 'none';
}

export interface ManagedFile {
  id: string;
  isNew: boolean;
  file?: File;
  url?: string;
  previewUrl?: SafeUrl;
  fileInfo: FileInfo;
  progress?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: any;
}

export class FileHelper {
  // ✅ Devuelve la extensión del archivo (en minúsculas)
  static getFileExtension(file: File): string {
    return file.name.split('.').pop()?.toLowerCase() || '';
  }

  // ✅ Devuelve el tipo MIME del archivo
  static getFileMimeType(file: File): string {
    return file.type;
  }

  // ✅ Devuelve el nombre sin la extensión
  static getFileNameWithoutExtension(file: File): string {
    return file.name.replace(/\.[^/.]+$/, '');
  }

  // ✅ Convierte tamaño en bytes a texto legible (ej. 2.1 MB)
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // ✅ Devuelve tipo de preview ('img', 'video', 'audio', 'iframe', 'none')
  static getPreviewType(file: File): 'img' | 'video' | 'audio' | 'iframe' | 'none' {
    const ext = FileHelper.getFileExtension(file);

    if (FileHelper.IMAGE_EXTENSIONS.includes(ext)) return 'img';
    if (FileHelper.VIDEO_EXTENSIONS.includes(ext)) return 'video';
    if (FileHelper.AUDIO_EXTENSIONS.includes(ext)) return 'audio';
    if (FileHelper.PDF_EXTENSIONS.includes(ext)) return 'iframe';

    return 'none';
  }

  // ✅ Retorna true si el archivo es una imagen
  static isImage(file: File): boolean {
    return FileHelper.IMAGE_EXTENSIONS.includes(FileHelper.getFileExtension(file));
  }

  // ✅ Retorna true si el archivo se puede previsualizar
  static isPreviewable(file: File): boolean {
    return FileHelper.getPreviewType(file) !== 'none';
  }

  // ✅ Retorna true si la extensión está permitida
  static isValidExtension(file: File, allowed: string[]): boolean {
    return allowed.includes(FileHelper.getFileExtension(file));
  }

  // ✅ Devuelve toda la info relevante de un archivo
  static getFileInfo(file: File): FileInfo {
    return {
      name: file.name,
      nameWithoutExtension: FileHelper.getFileNameWithoutExtension(file),
      extension: FileHelper.getFileExtension(file),
      mime: FileHelper.getFileMimeType(file),
      size: file.size,
      readableSize: FileHelper.formatFileSize(file.size),
      previewType: FileHelper.getPreviewType(file),
    };
  }

  // ✅ Devuelve la clase de icono de FontAwesome para una extensión
  static getIconForExtension(ext: string): string {
    if (FileHelper.IMAGE_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-image';
    if (FileHelper.PDF_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-pdf';
    if (FileHelper.WORD_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-word';
    if (FileHelper.EXCEL_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-excel';
    if (FileHelper.VIDEO_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-video';
    if (FileHelper.AUDIO_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-audio';
    if (FileHelper.COMPRESSED_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-zipper';
    if (FileHelper.TEXT_EXTENSIONS.includes(ext)) return 'fa-solid fa-file-lines';
    return 'fa-solid fa-file'; // Icono por defecto
  }

  // ============================
  // 📁 Creación de ManagedFile
  // ============================

  static fileInfoFromUrl(url: string): FileInfo {
    const name = url.split('/').pop()?.split('?')[0] || 'file';
    const dummyFile = new File([], name);
    return FileHelper.getFileInfo(dummyFile);
  }

  static createManagedFileFromFile(
    file: File,
    sanitizer: DomSanitizer,
    isNew: boolean,
    url?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata?: any
  ): Promise<ManagedFile> {
    return new Promise((resolve) => {
      const fileInfo = FileHelper.getFileInfo(file);
      const managedFile: ManagedFile = {
        id: uuidv4(),
        isNew,
        file,
        url,
        fileInfo,
        metadata,
      };

      if (fileInfo.previewType === 'img') {
        const reader = new FileReader();
        reader.onload = (e) => {
          managedFile.previewUrl = sanitizer.bypassSecurityTrustUrl(e.target?.result as string);
          resolve(managedFile);
        };
        reader.onerror = () => {
          resolve(managedFile);
        };
        reader.readAsDataURL(file);
      } else {
        resolve(managedFile);
      }
    });
  }

  static createManagedFileFromUrl(
    url: string,
    sanitizer: DomSanitizer,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata?: any
  ): ManagedFile {
    const fileInfo = FileHelper.fileInfoFromUrl(url);
    return {
      id: uuidv4(),
      isNew: false,
      url,
      fileInfo,
      metadata,
      previewUrl:
        fileInfo.previewType === 'img' ? sanitizer.bypassSecurityTrustUrl(url) : undefined,
    };
  }

  // ============================
  // 📁 Listas de extensiones
  // ============================
  static readonly IMAGE_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'webp',
    'svg',
    'heic',
    'heif',
  ];
  static readonly VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv'];
  static readonly AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'aac', 'flac'];
  static readonly WORD_EXTENSIONS = ['doc', 'docx'];
  static readonly EXCEL_EXTENSIONS = ['xls', 'xlsx', 'csv'];
  static readonly PDF_EXTENSIONS = ['pdf'];
  static readonly TEXT_EXTENSIONS = ['txt', 'md', 'log'];
  static readonly COMPRESSED_EXTENSIONS = ['zip', 'rar', '7z', 'tar', 'gz'];

  static readonly DOCUMENT_EXTENSIONS = [
    ...FileHelper.WORD_EXTENSIONS,
    ...FileHelper.EXCEL_EXTENSIONS,
    ...FileHelper.PDF_EXTENSIONS,
    ...FileHelper.TEXT_EXTENSIONS,
  ];

  static readonly MEDIA_EXTENSIONS = [
    ...FileHelper.IMAGE_EXTENSIONS,
    ...FileHelper.VIDEO_EXTENSIONS,
    ...FileHelper.AUDIO_EXTENSIONS,
  ];

  // ============================
  // 🧾 Strings para <input accept="...">
  // ============================
  static readonly ACCEPT_IMAGES = FileHelper.IMAGE_EXTENSIONS.map((ext) => '.' + ext).join(',');
  static readonly ACCEPT_VIDEOS = FileHelper.VIDEO_EXTENSIONS.map((ext) => '.' + ext).join(',');
  static readonly ACCEPT_DOCUMENTS = FileHelper.DOCUMENT_EXTENSIONS.map((ext) => '.' + ext).join(
    ','
  );
  static readonly ACCEPT_MEDIA = FileHelper.MEDIA_EXTENSIONS.map((ext) => '.' + ext).join(',');
  static readonly ACCEPT_ALL = [
    ...FileHelper.MEDIA_EXTENSIONS,
    ...FileHelper.DOCUMENT_EXTENSIONS,
    ...FileHelper.COMPRESSED_EXTENSIONS,
  ]
    .map((ext) => '.' + ext)
    .join(',');
}
